import { useMemo } from 'react';
import { useLoaderData } from 'react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  OrderListItem,
  OrderRepository,
  OrdersQuery,
} from 'src/api/order_repository';
import { orderKeys } from 'src/constants/orders';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { getOrdersQueryParams } from 'src/distribution_platforms/features/utils';

export type OrdersQueryKey = ReturnType<typeof orderKeys.list>;

interface UseOrdersReturn {
  orders: OrderListItem[];
  totalOrders: number;
}

export function useOrders(
  orderRepository: OrderRepository,
  searchParams: URLSearchParams,
  useLoader: boolean = true,
): UseOrdersReturn {
  const workflowType = useWorkflowType();
  const queryParams = getOrdersQueryParams(searchParams, workflowType);

  const loaderData = useLoaderData();

  // Create stable query key that matches loader structure
  const queryKey = useMemo(() => {
    if (useLoader && loaderData?.queryKey) {
      return loaderData.queryKey;
    }
    // Always use the same format as the loader
    return orderKeys.list(queryParams);
  }, [useLoader, loaderData?.queryKey, queryParams]);

  const { data } = useSuspenseQuery<OrdersQuery>({
    queryKey,
    queryFn: async () => {
      return orderRepository.getOrders(queryParams);
    },
    // Prevent refetching when in background mode
    refetchOnMount: useLoader,
    refetchOnWindowFocus: useLoader,
  });

  const orders = (data?.orders?.items ?? []).filter(
    (order): order is OrderListItem => order != null,
  );
  const totalOrders = data?.orders?.totalCount || 0;

  return {
    orders,
    totalOrders,
  };
}
